import * as _ from 'lodash';
import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../../../contexts/ToastContext';
import PublishedReportStatus from '../../../../shared/components/ReportStatus';
import { getSBOMPortalReportType } from '../../../releaseGroupLevel/utils';
import type { ReportCategory } from '../../../../shared/types';
import type { FormatName } from '../../types';

import { useUnpublishReport } from './hooks';

type Props = {
  format: FormatName | null;
  reportType: ReportCategory;
};

/**
 * Show the published report status:
 * 1. Show "Never published" if the report has never been published
 * 2. Show the date and time the report was published if it has been published
 * 3. Show pending if the report is being published
 * 4. Show the unpublish button if the report has been published
 */
const ReportStatus: VoidFunctionComponent<Props> = ({ reportType, format }) => {
  const addToast = useAddToast();
  const { unpublishReport, errors, isLoading, isUnpublished } = useUnpublishReport();
  useEffect(() => {
    if (!_.isEmpty(errors)) {
      addToast({ type: 'error', content: 'Failed to unpublish report' });
    }
  }, [addToast, errors]);
  // Don't show the report status if the report type is not supported
  // We currently only support SBOM reports and licensing plain text reports
  const sbomPortalReportType = getSBOMPortalReportType(reportType, format);
  if (!sbomPortalReportType) {
    return null;
  }

  return (
    <PublishedReportStatus
      onUnpublishReport={unpublishReport}
      isLoading={isLoading}
      isDisabled={isUnpublished}
      reportType={sbomPortalReportType}
    />
  );
};

export default ReportStatus;
